// Dripstone (M19.1). PURE (ENG-1): the extra fall damage a pointed-up stalagmite
// deals, the slow downward growth of a water-fed stalactite, and the drip that
// fills a cauldron beneath one. Pointed dripstone points `up` (a floor stalagmite)
// or `down` (a ceiling stalactite); the exposed end is the `tip`.
import { DRIPSTONE } from '../config';

/** Fall damage landing on an upward-pointing stalagmite tip: the base fall damage
 *  is amplified (MC ×2), so a fall onto a spike hurts far more. */
export function stalagmiteFallDamage(baseFallDamage: number): number {
  return Math.round(baseFallDamage * DRIPSTONE.FALL_MULTIPLIER);
}

/** A floor spike deals contact damage even without a fall (a mob walking into it). */
export function stalagmiteContactDamage(): number {
  return DRIPSTONE.CONTACT_DAMAGE;
}

/**
 * Whether a hanging stalactite tip at the bottom grows one block downward this
 * tick. Requires a dripstone_block capping the stalactite (fed from above by a
 * water source over that block — the drip source), the tip pointing down, and open
 * space (air) directly below. `roll01` gates it at MC's slow growth chance.
 */
export function dripstoneGrows(
  roll01: number,
  tipDirection: string,
  hasWaterFedCap: boolean,
  belowTip: string | null,
): boolean {
  if (roll01 >= DRIPSTONE.GROW_CHANCE) return false;
  if (tipDirection !== 'down' || !hasWaterFedCap) return false;
  return belowTip === null || belowTip === 'air';
}

/**
 * The fluid a stalactite drips into a cauldron below, or null. A water/lava source
 * two blocks above a downward stalactite drips its own fluid down; a cauldron in
 * the drip column then slowly fills with it.
 */
export function dripFluidInto(sourceAboveStalactite: string | null, tipDirection: string): 'water' | 'lava' | null {
  if (tipDirection !== 'down') return null;
  if (sourceAboveStalactite === 'water') return 'water';
  if (sourceAboveStalactite === 'lava') return 'lava';
  return null;
}

/** Whether a cauldron in the drip column may take on another level of `fluid`. */
export function cauldronDripFills(fluid: 'water' | 'lava' | null, cauldronLevel: number): boolean {
  return fluid !== null && cauldronLevel < DRIPSTONE.CAULDRON_MAX_LEVEL;
}

// ── Falling stalactites (M19.1 remainder) ────────────────────────────────────
// A stalactite hangs from what is above it. Break that support and the whole
// spike drops — the classic dripstone trap. Stalagmites (pointing up) rest on the
// floor and never fall.

/** Does a pointed dripstone at this cell come loose? Only a DOWN-pointing spike
 *  with nothing solid above it falls. */
export function stalactiteFalls(tipDirection: string, supportedAbove: boolean): boolean {
  return tipDirection === 'down' && !supportedAbove;
}

/**
 * Impact damage a falling stalactite deals to whatever it lands on: it accumulates
 * with the distance fallen and caps, so a spike from a cave roof is lethal but one
 * dislodged at head height is a bruise.
 */
export function stalactiteImpactDamage(fallDistance: number): number {
  if (fallDistance < DRIPSTONE.IMPACT_MIN_FALL) return 0;
  const dmg = Math.floor(fallDistance) * DRIPSTONE.IMPACT_PER_BLOCK;
  return Math.min(DRIPSTONE.IMPACT_MAX, dmg);
}

/** How tall the connected column of down-pointing dripstone under a broken support
 *  is — the whole run falls together, not just the tip. */
export function stalactiteColumn(
  isStalactiteAt: (dy: number) => boolean,
  maxLength: number = DRIPSTONE.MAX_COLUMN,
): number {
  let n = 0;
  while (n < maxLength && isStalactiteAt(-n)) n++;
  return n;
}
